import type { Atom } from "@pdfking/tfunc"
import type { IterableLike } from "../types/IterableLike"
import { Items } from "@pdfking/tfunc"
import { is_iterator } from "./is_iterator"
import { is_mapping } from "./is_mapping"
import { to_array } from "./to_array"

/**
 * Iterates over iterable applying function for side effects.
 *
 * Like forEach but works with objects, iterators, and arrays.
 * Function receives (value, key/index).
 *
 * @param f - Function to apply for each element
 * @param iterable - Array, object, iterator, or primitive
 *
 * @example
 * ```ts
 * import { scan } from "@pdfking/rfunc"
 *
 * scan((v, i) => console.log(v, i), [1, 2, 3])
 * scan((v, k) => console.log(k, v), { a: 1, b: 2 })
 * ```
 */
export function scan<T extends Atom | null | undefined>(
  f: (value: T, key: number) => void,
  iterable: T
): void
export function scan<T>(f: (value: T, key: number) => void, iterable: T[]): void
export function scan<T>(
  f: (value: T, key: number) => void,
  iterable: Iterable<T>
): void
export function scan<K extends string, T>(
  f: (value: T, key: K) => void,
  iterable: Record<K, T>
): void
export function scan<T>(
  f: (value: T, key: never) => void,
  iterable: IterableLike<T>
): void {
  if (is_iterator(iterable)) {
    let index = 0
    for (const value of iterable) {
      f(value as T, index as never)
      index++
    }
  } else if (is_mapping(iterable)) {
    Items(iterable).forEach(([k, v]) => f(v as T, k as never))
  } else {
    to_array(iterable).forEach(f)
  }
}
